import { Computer, Menu as MenuIcon, Dashboard, People, Image, AccountBalance, Logout, Groups, LibraryBooks } from '@mui/icons-material';
import { AppBar, Box, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemButton, ListItemText, ListItemIcon } from '@mui/material';
import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

const Header = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const navLinks = [
    { text: 'Dashboard', path: '/Admin/', icon: <Dashboard /> },
    { text: 'Members', path: '/Admin/submissions', icon: <People /> },
    { text: 'Executives', path: '/Admin/executives', icon: <Groups /> },
    { text: 'Gallery', path: '/Admin/gallery', icon: <Image /> },
    { text: 'Resources', path: '/Admin/resources', icon: <LibraryBooks /> },
    { text: 'Accounts', path: '/Admin/accounts', icon: <AccountBalance /> },
    { text: 'Logout', path: '/Client/', icon: <Logout /> }
  ];

  const isActive = (path) => {
    if (path === '/Admin/') {
      return location.pathname === '/Admin/' || location.pathname === '/Admin';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <AppBar
      sx={{
        background: 'rgba(10, 25, 41, 0.95)',
        backdropFilter: 'blur(20px)',
        boxShadow: '0px 4px 20px rgba(0, 217, 255, 0.3)',
        borderBottom: '1px solid rgba(0, 217, 255, 0.1)',
      }}
    >
      <Toolbar sx={{ py: 1 }}>
        {/* Logo & Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box
            sx={{
              width: 45,
              height: 45,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #00d9ff, #00b4d8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 15px rgba(0, 217, 255, 0.4)',
            }}
          >
            <Computer sx={{ fontSize: '28px', color: 'white' }} />
          </Box>
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: '700', fontSize: '20px', color: 'white' }}>
            ICTAZ NIPA
          </Typography>
          <Typography sx={{ fontSize: '12px', color: '#00d9ff', fontWeight: 500 }}>
            Admin Panel
          </Typography>
        </Box>

        {/* Navigation Links */}
        {isMobile ? (
          <>
            <IconButton
              onClick={toggleDrawer(true)}
              sx={{
                color: 'white',
                background: 'rgba(0, 217, 255, 0.1)',
                border: '1px solid rgba(0, 217, 255, 0.3)',
                '&:hover': {
                  background: 'rgba(0, 217, 255, 0.2)',
                }
              }}
            >
              <MenuIcon sx={{ fontSize: '28px' }} />
            </IconButton>
            <Drawer
              anchor="right"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
              PaperProps={{
                sx: {
                  width: 280,
                  background: 'rgba(0, 0, 36, 0.98)',
                  backdropFilter: 'blur(20px)',
                  borderLeft: '1px solid rgba(0, 217, 255, 0.2)',
                }
              }}
            >
              <Box sx={{ p: 3, borderBottom: '1px solid rgba(0, 217, 255, 0.2)' }}>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                  Admin Menu
                </Typography>
              </Box>
              <List sx={{ p: 2 }}>
                {navLinks.map((link) => (
                  <ListItem key={link.text} disablePadding sx={{ mb: 1 }}>
                    <ListItemButton
                      component={Link}
                      to={link.path}
                      onClick={toggleDrawer(false)}
                      sx={{
                        borderRadius: '12px',
                        py: 1.5,
                        background: isActive(link.path) ? 'rgba(0, 217, 255, 0.2)' : 'transparent',
                        border: isActive(link.path) ? '1px solid rgba(0, 217, 255, 0.4)' : '1px solid transparent',
                        "&:hover": {
                          background: 'rgba(0, 217, 255, 0.15)',
                          border: '1px solid rgba(0, 217, 255, 0.3)',
                        },
                      }}
                    >
                      <ListItemIcon sx={{ color: isActive(link.path) ? '#00d9ff' : 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                        {link.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={link.text}
                        sx={{
                          '& .MuiTypography-root': {
                            color: isActive(link.path) ? '#00d9ff' : 'white',
                            fontWeight: isActive(link.path) ? 600 : 400,
                          }
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {navLinks.map((link) => (
              <Link
                key={link.text}
                to={link.path}
                style={{ textDecoration: 'none' }}
              >
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    background: isActive(link.path) ? 'rgba(0, 217, 255, 0.2)' : 'transparent',
                    border: isActive(link.path) ? '1px solid rgba(0, 217, 255, 0.4)' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      background: 'rgba(0, 217, 255, 0.15)',
                      border: '1px solid rgba(0, 217, 255, 0.3)',
                    },
                  }}
                >
                  <Box sx={{ color: isActive(link.path) ? '#00d9ff' : 'rgba(255,255,255,0.7)', display: 'flex' }}>
                    {link.icon}
                  </Box>
                  <Typography
                    sx={{
                      color: isActive(link.path) ? '#00d9ff' : 'white',
                      fontWeight: isActive(link.path) ? 600 : 500,
                      fontSize: '0.95rem',
                    }}
                  >
                    {link.text}
                  </Typography>
                </Box>
              </Link>
            ))}
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
